import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { RootStackParamList } from '../navigation/types';

/**
 * CORE-1343 — кто сейчас в эфире на этом устройстве.
 *
 * Хранит ТОЛЬКО удостоверение сессии — параметры комнаты, с которыми её
 * открыли. Само состояние эфира (хаб, RTC, чат) живёт в `LiveSessionHost`
 * рядом с навигацией: если бы `useLiveRoom` крутился здесь, каждое сообщение
 * чата перерисовывало бы всё приложение.
 *
 * Режим (развёрнута / свёрнута) здесь тоже НЕ хранится: он выводится из
 * навигации — см. `resolveShellMode`. Своё состояние пришлось бы
 * синхронизировать с каждым переходом, а расхождение означало бы плитку
 * поверх открытой комнаты.
 */

export type LiveSessionParams = RootStackParamList['LiveRoomSession'];

interface LiveSessionShell {
  /** Параметры идущей сессии, либо null — сессии нет. */
  params: LiveSessionParams | null;
  /** Начать сессию (или переоткрыть ту же комнату), не трогая идущую другую. */
  open: (params: LiveSessionParams) => void;
  /** Сессии на этом устройстве больше нет: выход, завершение, отказ сервера. */
  close: () => void;
}

const Ctx = createContext<LiveSessionShell | null>(null);

export const LiveSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [params, setParams] = useState<LiveSessionParams | null>(null);

  const open = useCallback((next: LiveSessionParams) => {
    setParams((prev) => {
      // Тот же id — та же сессия: возврат в комнату из плитки не должен
      // пересоздавать состояние и переподключать RTC.
      if (prev && prev.roomId === next.roomId) return prev;
      return next;
    });
  }, []);

  const close = useCallback(() => setParams(null), []);

  const value = useMemo(() => ({ params, open, close }), [params, open, close]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useLiveSessionShell(): LiveSessionShell {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLiveSessionShell вызван вне LiveSessionProvider');
  return ctx;
}
